/**
 * A Company's brand colour as the app's own `--primary` token, for the
 * Company-branded sign-in (ADR-024 S10).
 *
 * The app's tokens are HSL triplets (`--primary: 0 0% 9%`) behind resolved
 * `--color-*` tokens. Buttons, links and focus rings read those, so overriding
 * both on a wrapper recolours exactly the primary affordances inside it —
 * nothing is threaded through components.
 *
 * Legibility decides whether the override applies at all: `--primary` is also
 * used as TEXT (links), so a brand colour that would vanish against the
 * current theme's background is not applied — the page keeps the Fias
 * default rather than rendering the brand unreadably.
 */
import type { CSSProperties } from 'react';
import { isLegibleOn, onColor, THEME_BACKGROUND } from './brand-color';

const HEX = /^#[0-9a-fA-F]{6}$/;

/** `#rrggbb` → `h s% l%`, the format the theme tokens use. */
export function hexToHslTriplet(hex: string): string {
  const v = hex.replace('#', '');
  const r = parseInt(v.slice(0, 2), 16) / 255;
  const g = parseInt(v.slice(2, 4), 16) / 255;
  const b = parseInt(v.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
  }
  const round = (n: number) => Math.round(n * 10) / 10;
  return `${round(h)} ${round(s * 100)}% ${round(l * 100)}%`;
}

/**
 * WCAG AA for normal-size text. The same token colours buttons (where 3:1
 * would do) and normal-size links ("Sign up", "Forgot password?"), so it must
 * meet the text bar; one token for both keeps the brand consistent.
 */
const TEXT_CONTRAST = 4.5;

/**
 * The CSS variables that put `primary` on the primary affordances, or nothing
 * when it is missing, malformed, or not legible as normal text on this theme
 * (WCAG 4.5:1). A colour that fails keeps the Fias default.
 */
export function brandPrimaryVars(
  primary: string | null | undefined,
  theme: 'light' | 'dark',
): CSSProperties {
  if (!primary || !HEX.test(primary)) return {};
  if (!isLegibleOn(primary, THEME_BACKGROUND[theme], TEXT_CONTRAST)) return {};
  const brand = hexToHslTriplet(primary);
  const on = hexToHslTriplet(onColor(primary));
  return {
    // The raw triplets, for anything that reads `hsl(var(--primary))` itself…
    ['--primary' as string]: brand,
    ['--primary-foreground' as string]: on,
    ['--ring' as string]: brand,
    // …and the resolved tokens Tailwind's utilities read (`bg-primary` is
    // `var(--color-primary)`). index.css declares those in `@theme`, which
    // resolves `hsl(var(--primary))` once at :root; overriding only the
    // triplet on a wrapper never reaches them.
    ['--color-primary' as string]: `hsl(${brand})`,
    ['--color-primary-foreground' as string]: `hsl(${on})`,
    ['--color-ring' as string]: `hsl(${brand})`,
  } as CSSProperties;
}
